import React, { createContext, useContext, useState, useMemo, useCallback } from "react";

const CartContext = createContext(null);

// Versioned key; bumping the version invalidates any stale carts on the client.
const STORAGE_KEY = "nournet.cart.v2";
// 24-hour TTL — carts older than this are dropped on load.
const TTL_MS = 24 * 60 * 60 * 1000;

const safeLoad = () => {
    try {
        if (typeof window === "undefined") return [];
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return [];
        const { savedAt, items } = parsed;
        if (!Array.isArray(items)) return [];
        if (typeof savedAt !== "number" || Date.now() - savedAt > TTL_MS) {
            window.localStorage.removeItem(STORAGE_KEY);
            return [];
        }
        return items;
    } catch (err) {
        if (typeof console !== "undefined") console.warn("cart: failed to load", err);
        return [];
    }
};

const safeSave = (items) => {
    try {
        if (typeof window === "undefined") return;
        window.localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ savedAt: Date.now(), items })
        );
    } catch (err) {
        if (typeof console !== "undefined") console.warn("cart: failed to persist", err);
    }
};

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(safeLoad);

    const persist = useCallback((next) => {
        setItems(next);
        safeSave(next);
    }, []);

    const add = useCallback((item) => {
        setItems((prev) => {
            if (prev.find((p) => p.domain === item.domain)) return prev;
            const next = [...prev, item];
            safeSave(next);
            return next;
        });
    }, []);

    const remove = useCallback((domain) => {
        setItems((prev) => {
            const next = prev.filter((p) => p.domain !== domain);
            safeSave(next);
            return next;
        });
    }, []);

    const clear = useCallback(() => persist([]), [persist]);

    const total = useMemo(
        () => items.reduce((s, i) => s + (i.price_sar || 0), 0),
        [items]
    );

    const value = useMemo(
        () => ({ items, add, remove, clear, total, count: items.length }),
        [items, add, remove, clear, total]
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used inside CartProvider");
    return ctx;
};
